import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import { routeTree } from '@/routeTree.gen'
import { getQueryClient } from '@/lib/query-client'
import { NotFoundScreen } from '@/components/shell/not-found-screen'
import { RouteErrorScreen } from '@/components/shell/route-error-screen'
import '@/globals.css'

/*
 * One client for the whole app, created before the router so it can be handed
 * to route loaders as context. `getQueryClient` memoizes, so the components
 * that call it directly get this same instance.
 */
const queryClient = getQueryClient()

const router = createRouter({
  routeTree,
  context: { queryClient },
  /*
   * Prefetch a route's code and loader on hover/touch, which is what makes the
   * sidebar feel instant — by the time the click lands the chunk is usually
   * parsed and the queries are in flight.
   */
  defaultPreload: 'intent',
  /*
   * Zero, deliberately, and it does NOT mean "refetch constantly".
   *
   * This is the router's own cache of loader results, sitting in front of React
   * Query's. Every loader here does nothing but call `prefetchQuery`/
   * `ensureQueryData`, so React Query is the thing that actually decides
   * whether a request goes out — and it already has a considered `staleTime`
   * (see query-client.ts). Letting the router hold a second, shorter-lived
   * opinion about freshness would mean two caches disagreeing about the same
   * data, with the router's winning on navigation. So the router keeps none and
   * defers.
   */
  defaultPreloadStaleTime: 0,
  defaultNotFoundComponent: NotFoundScreen,
  defaultErrorComponent: RouteErrorScreen,
  scrollRestoration: true,
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

const container = document.getElementById('root')
if (!container) throw new Error('index.html is missing #root')

createRoot(container).render(
  <StrictMode>
    <RouterProvider router={router} />
  </StrictMode>,
)
